/**
 * Pure HTML rendering functions
 * Generate HTML strings without DOM manipulation.
 *
 * Non-creators now render a stable base row shell with empty rank/change/trend cells
 * when deferContextHydration is enabled. Visible context cells are hydrated later.
 */

import { getCategoryDisplayMode, getColumnsForType } from "./columns.js";
import { escapeHtml, log } from "./core.js";
import { STATE_TO_URL_TYPE, state } from "./dom.js";
import { isFieldEnabled } from "./features.js";
import { formatTopPercentDisplay } from "./formatters.js";
import { extractCategoryTooltipHtml, stripCategoryTooltipHtml } from "./row-compact.js";

const DEFAULT_CREATOR_AVATAR_URL = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" role="img" aria-label="Creator avatar">
    <rect width="40" height="40" rx="20" fill="#2a3240"/>
    <circle cx="20" cy="15" r="7" fill="#95a2b6"/>
    <path d="M8 35c1.5-6.2 6.9-10 12-10s10.5 3.8 12 10" fill="#95a2b6"/>
  </svg>`,
)}`;
const DEFERRED_THUMBNAIL_PLACEHOLDER_URL = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 48"></svg>`,
)}`;
function renderMarketplaceLinkedText(value) {
  const text = String(value ?? "");
  const match = text.match(/^(.*\s)(\S+)$/u);
  if (!match) {
    return `<span class="marketplace-link-tail">${escapeHtml(text)}</span>`;
  }
  return `${escapeHtml(match[1])}<span class="marketplace-link-tail">${escapeHtml(match[2])}</span>`;
}

function normalizeCreatorAvatarUrl(value, { width = 32, quality = 90 } = {}) {
  if (typeof value !== "string") return DEFAULT_CREATOR_AVATAR_URL;
  const trimmed = value.trim();
  if (!trimmed) return DEFAULT_CREATOR_AVATAR_URL;
  if (trimmed.startsWith("data:")) return trimmed;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return optimizeFramerImageUrl(trimmed, { width, quality });
  }
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  if (trimmed.startsWith("/")) return `https://www.framer.com${trimmed}`;
  return DEFAULT_CREATOR_AVATAR_URL;
}

function formatExpertCountry(value) {
  const code = typeof value === "string" ? value.trim().toUpperCase() : "";
  if (!code) return "-";
  if (code === "GB") return "UK";
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code;
  } catch {
    return code;
  }
}

function formatExpertType(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "studio") return "Studio";
  if (normalized === "freelancer") return "Freelancer";
  if (normalized === "pro") return "Pro";
  return normalized.replace(/[-_]+/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function renderExpertTypes(item) {
  const types = Array.isArray(item.expertTypes) ? item.expertTypes : [];
  const type = types.find((value) => value === "studio" || value === "freelancer");
  if (!type) return '<span class="expert-type-empty">-</span>';
  return renderExpertTypeContextButton(item, type);
}

function renderExpertPro(item) {
  if (!Array.isArray(item.expertTypes) || !item.expertTypes.includes("pro")) {
    return '<span class="expert-type-empty">-</span>';
  }
  return renderExpertTypeContextButton(item, "pro");
}

function renderExpertTypeContextButton(item, type) {
  const context = `expert-type-${type}-all`;
  const rank = item.ranks?.[context]?.[0];
  const rankHtml = formatCategoryRankWithMedal(rank, 12, "expert-type-rank-medal");
  return `<button type="button" class="rank-context-jump-btn expert-type-context-jump-btn" data-expert-type="${escapeHtml(type)}" data-item-id="${escapeHtml(item.id)}" data-item-type="expert"><span class="rank-context-name expert-type-pill">${escapeHtml(formatExpertType(type))}</span>${rankHtml ? `<span class="rank-context-rank expert-type-context-rank">${rankHtml}</span>` : ""}</button>`;
}

function renderExpertAvailability(item) {
  return Array.isArray(item.badges) && item.badges.includes("available")
    ? '<span class="expert-availability" data-tooltip="Available" aria-label="Available"></span>'
    : "";
}

function optimizeFramerImageUrl(value, { width, quality }) {
  if (typeof value !== "string" || !value.trim()) return value;
  const trimmed = value.trim();
  if (trimmed.includes("/creators-assets/_next/image/")) {
    try {
      const parsed = new URL(trimmed);
      parsed.searchParams.set("w", String(width));
      parsed.searchParams.set("q", String(quality));
      return parsed.toString();
    } catch {
      return trimmed;
    }
  }

  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch {
    return trimmed;
  }
  if (parsed.hostname === "image.mux.com") {
    parsed.searchParams.set("width", String(width));
    parsed.searchParams.set("height", width === 64 ? "48" : String(width));
    parsed.searchParams.set("fit", "crop");
    return parsed.toString();
  }
  if (!parsed.hostname.endsWith(".public.blob.vercel-storage.com")) return trimmed;

  const optimizerUrl = new URL("https://www.framer.com/creators-assets/_next/image/");
  optimizerUrl.searchParams.set("url", trimmed);
  optimizerUrl.searchParams.set("w", String(width));
  optimizerUrl.searchParams.set("q", String(quality));
  return optimizerUrl.toString();
}

function normalizeMarketplaceThumbnailUrl(value, { width = 64, quality = 90 } = {}) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || /\.m3u8(?:$|\?)/i.test(trimmed)) return null;
  return optimizeFramerImageUrl(trimmed, { width, quality });
}

function getCategoryLabel(categoryId) {
  const normalizeCategoryDisplayName = (name) => {
    const original = String(name || "").trim();
    if (!original) return "";
    const stripped = original.replace(/\s*Templates?\s*$/i, "").replace(/\s*Components?\s*$/i, "");
    const normalized = stripped
      .replace(/\bCms\b/g, "CMS")
      .replace(/\bAi\b/g, "AI")
      .replace(/\bUi\b/g, "UI")
      .replace(/\bSeo\b/g, "SEO")
      .trim();
    return normalized || original;
  };

  if (typeof window !== "undefined") {
    const metaName = window.__RANKS_CATEGORY_META__?.[categoryId]?.name;
    if (metaName) return normalizeCategoryDisplayName(metaName);
  }

  const lastSegment = String(categoryId).split("/").filter(Boolean).pop() || String(categoryId);
  return normalizeCategoryDisplayName(lastSegment.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));
}

function getItemTypeLabel(itemType) {
  if (itemType === "component") return "Components";
  if (itemType === "plugin") return "Plugins";
  if (itemType === "vector") return "Vectors";
  if (itemType === "site") return "Sites";
  if (itemType === "tutorial") return "Tutorials";
  if (itemType === "expert") return "Experts";
  return "Templates";
}

function getCategoryOrderingHelpers(renderOptions = {}) {
  const categoryOrderByType = renderOptions.categoryOrderByType || {};
  const categoryDepthByType = renderOptions.categoryDepthByType || {};
  const fallbackMetaByType = renderOptions.categoryMetaByType || {};
  return { categoryOrderByType, categoryDepthByType, fallbackMetaByType };
}

function getOrderedCategoriesForItem(item, renderOptions = {}) {
  const categoryIds = Array.isArray(item?.categories) ? item.categories.filter(Boolean) : [];
  if (categoryIds.length === 0) return [];

  const { categoryOrderByType, categoryDepthByType } = getCategoryOrderingHelpers(renderOptions);
  const orderMap = categoryOrderByType[item.type] || {};
  const depthMap = categoryDepthByType[item.type] || {};

  const uniqueCategoryIds = [...new Set(categoryIds)];
  if (renderOptions.preserveItemCategoryOrder) {
    return uniqueCategoryIds.map((categoryId, index) => ({
      categoryId,
      order: index,
      depth: Number.isFinite(depthMap[categoryId]) ? depthMap[categoryId] : 0,
    }));
  }

  return uniqueCategoryIds
    .map((categoryId) => ({
      categoryId,
      order: Number.isFinite(orderMap[categoryId]) ? orderMap[categoryId] : Number.MAX_SAFE_INTEGER,
      depth: Number.isFinite(depthMap[categoryId]) ? depthMap[categoryId] : 0,
    }))
    .sort((a, b) => a.order - b.order || a.categoryId.localeCompare(b.categoryId));
}

function getCategoryLabelForType(categoryId, itemType, renderOptions = {}) {
  const normalizeCategoryDisplayName = (name) => {
    const original = String(name || "").trim();
    if (!original) return "";
    const stripped = original.replace(/\s*Templates?\s*$/i, "").replace(/\s*Components?\s*$/i, "");
    const normalized = stripped
      .replace(/\bCms\b/g, "CMS")
      .replace(/\bAi\b/g, "AI")
      .replace(/\bUi\b/g, "UI")
      .replace(/\bSeo\b/g, "SEO")
      .trim();
    return normalized || original;
  };

  const { fallbackMetaByType } = getCategoryOrderingHelpers(renderOptions);
  const fallbackMeta = fallbackMetaByType[itemType] || {};
  const metaName = fallbackMeta[categoryId]?.name;
  if (metaName) return normalizeCategoryDisplayName(metaName);
  return getCategoryLabel(categoryId);
}

function getTemplateCategoryDepthPrefix(depth) {
  if (!Number.isFinite(depth) || depth <= 1) return "";
  return `${"-".repeat(depth - 1)} `;
}

function formatCategoryTooltipRank(rank) {
  return typeof rank === "number" && Number.isFinite(rank) ? `#${rank.toLocaleString()}` : "";
}

function getMainCategoryRankContext(categoryId, activeContext = "global-all") {
  const context = String(activeContext || "global-all");
  const pricing = context.endsWith("-free") ? "free" : context.endsWith("-paid") ? "paid" : "all";
  const modePrefix = context.startsWith("best30-") ? "best30-" : context.startsWith("best90-") ? "best90-" : "";
  return `${modePrefix}${categoryId}-${pricing}`;
}

function renderMainCategoryRankCell(item, renderOptions = {}) {
  const mainCategory = typeof item?.mainCategory === "string" ? item.mainCategory.trim() : "";
  if (!mainCategory) return "";

  const categoryLabel = getCategoryLabelForType(mainCategory, "template", renderOptions);
  const rankContext = getMainCategoryRankContext(mainCategory, renderOptions.activeContext);
  const rank = item.ranks?.[rankContext]?.[0];
  const rankText = formatCategoryRankWithMedal(rank, 12, "main-category-rank-medal");
  return `<button type="button" class="rank-context-jump-btn main-category-rank-btn category-jump-btn" data-category-id="${escapeHtml(mainCategory)}" data-item-id="${escapeHtml(item.id)}" data-item-type="${escapeHtml(item.type)}"><span class="rank-context-name main-category-name">${escapeHtml(categoryLabel)}</span><span class="rank-context-rank main-category-rank">${rankText}</span></button>`;
}

function formatCategoryRankWithMedal(rank, size = 12, extraClass = "") {
  if (rank === null || rank === undefined || rank === "") return "";
  const numericRank = Number(rank);
  if (!Number.isFinite(numericRank)) return "";
  const rankText = `#${numericRank.toLocaleString()}`;
  if (numericRank < 1 || numericRank > 3) return rankText;
  return `<span class="category-rank-with-medal">${renderMedalSvg(numericRank, size, extraClass)}<span class="category-rank-number">${rankText}</span></span>`;
}

function renderCategoryTooltipButton({ categoryId, depth }, item, renderOptions = {}) {
  const categoryLabel = getCategoryLabelForType(categoryId, item.type, renderOptions);
  const templateDepthPrefix = item.type === "template" ? getTemplateCategoryDepthPrefix(depth) : "";
  const templateDepthPrefixHtml = templateDepthPrefix
    ? `<span class="category-depth-prefix" aria-hidden="true">${escapeHtml(templateDepthPrefix)}</span>`
    : "";
  const childClass = depth > 1 ? " tooltip-item-child" : "";
  const rankAll = formatCategoryTooltipRank(item.ranks?.[`${categoryId}-all`]?.[0] ?? null);
  const rankFree = formatCategoryTooltipRank(item.ranks?.[`${categoryId}-free`]?.[0] ?? null);
  const rankPaid = formatCategoryTooltipRank(item.ranks?.[`${categoryId}-paid`]?.[0] ?? null);
  const rankAllHtml = formatCategoryRankWithMedal(
    item.ranks?.[`${categoryId}-all`]?.[0] ?? null,
    12,
    "tooltip-medal-svg",
  );
  const rankFreeHtml = formatCategoryRankWithMedal(
    item.ranks?.[`${categoryId}-free`]?.[0] ?? null,
    12,
    "tooltip-medal-svg",
  );
  const rankPaidHtml = formatCategoryRankWithMedal(
    item.ranks?.[`${categoryId}-paid`]?.[0] ?? null,
    12,
    "tooltip-medal-svg",
  );
  return `<button type="button" class="tooltip-item category-jump-btn${childClass}" data-category-id="${escapeHtml(categoryId)}" data-item-id="${escapeHtml(item.id)}" data-item-type="${item.type}">${templateDepthPrefixHtml}<span class="category-rank"><span class="crr-all">${rankAllHtml || rankAll}</span><span class="crr-free">${rankFreeHtml || rankFree}</span><span class="crr-paid">${rankPaidHtml || rankPaid}</span></span> in ${escapeHtml(categoryLabel)}</button>`;
}

function buildTemplateCategoryTooltipTree(orderedCategories, item, renderOptions = {}) {
  const roots = [];
  const stack = [];

  for (const entry of orderedCategories) {
    const node = {
      ...entry,
      buttonHtml: renderCategoryTooltipButton(entry, item, renderOptions),
      children: [],
    };

    while (stack.length > 0 && stack[stack.length - 1].depth >= entry.depth) {
      stack.pop();
    }

    if (stack.length > 0) {
      stack[stack.length - 1].children.push(node);
    } else {
      roots.push(node);
    }

    stack.push(node);
  }

  return roots;
}

function renderTemplateCategoryTooltipTree(nodes) {
  const renderNode = (node) => {
    const hasChildren = node.children.length > 0;
    return `<div class="tooltip-node${hasChildren ? " has-children" : ""}">${node.buttonHtml}${hasChildren ? `<div class="children">${node.children.map(renderNode).join("")}</div>` : ""}</div>`;
  };

  return `<div class="tooltip-tree">${nodes.map(renderNode).join("")}</div>`;
}

export function renderCategoryCellContent(item, renderOptions = {}) {
  const categoryDisplayMode = renderOptions.categoryDisplayMode || getCategoryDisplayMode(item?.type, [item]);
  if (categoryDisplayMode === "main-category-rank" && item?.type === "template") {
    return renderMainCategoryRankCell(item, renderOptions);
  }

  const categoryOrderOptions =
    item?.type === "template" ? renderOptions : { ...renderOptions, preserveItemCategoryOrder: true };
  const orderedCategories = getOrderedCategoriesForItem(item, categoryOrderOptions);
  const categoriesCount = orderedCategories.length;
  if (categoriesCount === 0) {
    return item?.type === "template" ? '<span class="categories-summary">0</span>' : "";
  }

  const tooltipItems =
    item.type === "template"
      ? renderTemplateCategoryTooltipTree(buildTemplateCategoryTooltipTree(orderedCategories, item, renderOptions))
      : orderedCategories.map((entry) => renderCategoryTooltipButton(entry, item, renderOptions)).join("");

  if (categoryDisplayMode === "count") {
    return `<div class="with-tooltip"><span class="categories-summary">${categoriesCount.toLocaleString()}</span><div class="tooltip right"><div class="tooltip-title">Categories</div>${tooltipItems}</div></div>`;
  }

  if (categoryDisplayMode === "stacked") {
    const categoryLabels = orderedCategories
      .map((entry) => getCategoryLabelForType(entry.categoryId, item.type, renderOptions))
      .map((label) => `<span class="categories-stacked-item">${escapeHtml(label)}</span>`)
      .join("");
    return `<div class="with-tooltip"><span class="categories-stacked">${categoryLabels}</span><div class="tooltip right"><div class="tooltip-title">Categories</div>${tooltipItems}</div></div>`;
  }

  const firstCategory = orderedCategories[0];
  const categoryLabel = getCategoryLabelForType(firstCategory.categoryId, item.type, renderOptions);
  return `<div class="with-tooltip"><span class="categories-single">${escapeHtml(categoryLabel)}</span><div class="tooltip right"><div class="tooltip-title">Category</div>${tooltipItems}</div></div>`;
}

export function renderCategoryTooltipHtml(item, renderOptions = {}) {
  if (!item) return "";
  return extractCategoryTooltipHtml(renderCategoryCellContent(item, renderOptions));
}

/**
 * Normalize numeric trend values while preserving relative shape.
 * Null/undefined entries are preserved as gaps.
 * This enables obfuscating absolute ranks without changing sparkline geometry.
 *
 * @param {Array<number|null|undefined>} trendData
 * @param {{targetMin?: number, targetMax?: number}} options
 * @returns {Array<number|null>}
 */
export function normalizeTrendForObfuscation(trendData, options = {}) {
  if (!Array.isArray(trendData)) return [];

  const targetMin = Number.isFinite(options.targetMin) ? options.targetMin : 0;
  const targetMax = Number.isFinite(options.targetMax) ? options.targetMax : 100;
  const numericValues = trendData.filter((v) => typeof v === "number" && Number.isFinite(v));
  if (numericValues.length === 0) {
    return trendData.map((v) => (v === null || v === undefined ? null : null));
  }

  const sourceMin = Math.min(...numericValues);
  const sourceMax = Math.max(...numericValues);
  const sourceRange = sourceMax - sourceMin;
  const targetRange = targetMax - targetMin;
  const midpoint = targetMin + targetRange / 2;

  return trendData.map((value) => {
    if (value === null || value === undefined) return null;
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    if (sourceRange === 0) return Number(midpoint.toFixed(4));
    const normalized = targetMin + ((num - sourceMin) / sourceRange) * targetRange;
    return Number(normalized.toFixed(4));
  });
}

function shouldRenderSparklinePointLabels() {
  if (typeof window === "undefined") return "none";
  const mode = window.__RANKS_SPARKLINE_LABEL_MODE__;
  return mode === "all" ? "all" : "none";
}

function shouldRenderSparklineTitleAttr() {
  if (typeof window === "undefined") return false;
  return window.__RANKS_SPARKLINE_TITLE_ATTR__ === true;
}

function renderMedalSvg(_rank, _size = 14, _extraClass = "") {
  const rank = Number(_rank);
  const medalRank = rank === 1 || rank === 2 || rank === 3 ? rank : 3;
  const tone = medalRank === 1 ? "gold" : medalRank === 2 ? "silver" : "bronze";
  const classSuffix = _extraClass ? ` ${_extraClass}` : "";
  return `<span class="medal-svg medal-rank-${medalRank}${classSuffix}"><svg class="inline-award-svg inline-medal-svg" data-award-rank="${medalRank}" width="${_size}" height="${_size}" aria-hidden="true" focusable="false"><use href="#award-medal-${tone}"></use></svg></span>`;
}

function renderTrophySvg(_rank, _size = 16, _extraClass = "") {
  return "";
}

/**
 * Compute crowns for an item
 * Returns badges.crowns as-is, except tutorials which are deduped to 1 crown.
 * @param {Object} item - The item with ranks data
 * @returns {Array<{period: string, market: string}>}
 */
export function computeCrowns(item) {
  const crowns = Array.isArray(item?.badges?.crowns) ? item.badges.crowns : [];
  if (item?.type !== "tutorial" || crowns.length <= 1) return crowns;

  // Tutorials are single-lane in UI: keep only one lastWeek crown.
  const tutorialCrown = crowns.find((crown) => crown?.period === "lastWeek") || crowns[0];
  return tutorialCrown ? [tutorialCrown] : [];
}

function getGlobalTrophyContextsForItem(item) {
  if (!item || item.type === "creator") return [];
  if (item.type === "tutorial") {
    return [{ key: "lastWeek-all", period: "lastWeek", market: "all" }];
  }
  if (item.type === "plugin") {
    return [
      { key: "alltime-free", period: "alltime", market: "free" },
      { key: "alltime-paid", period: "alltime", market: "paid" },
    ];
  }
  return [
    { key: "lastWeek-free", period: "lastWeek", market: "free" },
    { key: "lastWeek-paid", period: "lastWeek", market: "paid" },
  ];
}

function getGlobalTrophiesForItem(item) {
  if (!item || item.type === "creator") return [];
  const contexts = getGlobalTrophyContextsForItem(item);

  const trophies = contexts.flatMap((context) => {
    const rank = Number(item?.ranks?.[context.key]?.[0]);
    if (!Number.isFinite(rank) || rank < 1 || rank > 3) return [];
    return [{ ...context, rank }];
  });

  // Keep at most one icon per rank in name column (gold/silver/bronze),
  // preserving first context match priority from the ordered list above.
  const seenRanks = new Set();
  return trophies.filter((entry) => {
    if (seenRanks.has(entry.rank)) return false;
    seenRanks.add(entry.rank);
    return true;
  });
}

/**
 * Compute medals for an item (templates only)
 * Returns medals for category ranks 1, 2, or 3
 * @param {Object} item - The item with ranks data
 * @returns {Array<{icon: string, rank: number, category: string}>}
 */
export function computeMedals(item) {
  const medals = Array.isArray(item?.badges?.medals) ? item.badges.medals : [];
  return medals.map((entry) => ({
    icon: entry.rank === 1 ? "🥇" : entry.rank === 2 ? "🥈" : "🥉",
    rank: entry.rank,
    category: entry.categoryId,
    categoryLabel: getCategoryLabel(entry.categoryId),
    period: entry.period,
    market: entry.market,
  }));
}

/**
 * Get medal score (gold=3, silver=2, bronze=1)
 * @param {Array<{icon: string, rank: number, category: string}>} medals
 * @returns {number}
 */
export function getMedalScore(medals) {
  return (Array.isArray(medals) ? medals : []).reduce((score, medal) => {
    if (medal.rank === 1) return score + 3;
    if (medal.rank === 2) return score + 2;
    if (medal.rank === 3) return score + 1;
    return score;
  }, 0);
}

/**
 * Format a change value for display
 * @param {number|string|null} change - The change value (number, "new", "moved-in", "comeback", "price_change", or null)
 * @param {string} context - The context for price_change labeling ("free" or "paid")
 * @returns {{text: string, colorClass: string, title: string}}
 */
function formatChange(change, context = "all") {
  if (change === "new") {
    return { text: "new", colorClass: "text-lime-400", title: "New to the marketplace" };
  }
  if (change === "moved-in") {
    return { text: "moved-in", colorClass: "text-cyan-400", title: "Moved into this category" };
  }
  if (change === "comeback") {
    return { text: "comeback", colorClass: "text-amber-500", title: "Was previously unlisted, now back" };
  }
  if (change === "price_change") {
    const title =
      context === "paid" ? "Was free, now paid" : context === "free" ? "Was paid, now free" : "Price changed";
    return { text: "repriced", colorClass: "text-purple-400", title };
  }
  if (change === null || change === undefined) {
    return { text: "-", colorClass: "text-muted-foreground", title: "" };
  }
  if (typeof change === "number") {
    if (change > 0) {
      return {
        text:
          '<span class="delta-marker delta-up" aria-hidden="true"><svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><path d="M5 1 L9 8 L1 8 Z" fill="currentColor"/></svg></span>' +
          String(change),
        colorClass: "text-green-600",
        title: "",
      };
    }
    if (change < 0) {
      return {
        text:
          '<span class="delta-marker delta-down" aria-hidden="true"><svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><path d="M5 1 L9 8 L1 8 Z" fill="currentColor"/></svg></span>' +
          String(Math.abs(change)),
        colorClass: "text-red-600",
        title: "",
      };
    }
    return { text: "0", colorClass: "text-muted-foreground", title: "" };
  }
  return { text: "-", colorClass: "text-muted-foreground", title: "" };
}

function formatCreatorDelta(deltaValue) {
  if (deltaValue === null || deltaValue === undefined) {
    return '<span class="text-muted-foreground">-</span>';
  }
  const delta = Number(deltaValue);
  if (!Number.isFinite(delta)) {
    return '<span class="text-muted-foreground">-</span>';
  }
  if (delta > 0) {
    return `<span class="text-green-600">+${delta.toLocaleString()}</span>`;
  }
  if (delta < 0) {
    return `<span class="text-red-600">${delta.toLocaleString()}</span>`;
  }
  return '<span class="text-muted-foreground creator-zero-muted">0</span>';
}

function formatCreatorMetricCount(value) {
  const count = Number(value || 0);
  const text = count.toLocaleString();
  if (count === 0) {
    return `<span class="text-muted-foreground creator-zero-muted">${text}</span>`;
  }
  return text;
}

/**
 * Format a rank value for display
 * @param {number|null} rank - The rank value
 * @returns {string}
 */
function formatRank(rank) {
  if (rank === null || rank === undefined) {
    return "-";
  }
  return `#${rank}`;
}

function renderTopPercent(rank, total) {
  const text = formatTopPercentDisplay(rank, total);
  if (!text) return '<span class="text-muted-foreground">-</span>';
  const rankNumber = Number(rank);
  const totalNumber = Number(total);
  return `<span class="toppercent-value" title="#${rankNumber.toLocaleString()} of ${totalNumber.toLocaleString()}">${escapeHtml(text)}</span>`;
}

function parsePriceToNumber(priceText) {
  if (typeof priceText !== "string") return null;
  const trimmed = priceText.trim();
  if (/^free$/i.test(trimmed)) return 0;
  const match = trimmed.match(/\$(\d+)/);
  if (!match) return null;
  return Number.parseInt(match[1], 10);
}

function resolvePreviousPriceNumber(currentPrice, previousPrice) {
  const explicitPreviousNumeric =
    typeof previousPrice === "number" ? previousPrice : parsePriceToNumber(previousPrice || "");
  if (Number.isFinite(explicitPreviousNumeric)) {
    const currentNumeric = parsePriceToNumber(currentPrice);
    if (Number.isFinite(currentNumeric) && currentNumeric === explicitPreviousNumeric) return null;
    return explicitPreviousNumeric;
  }
  return null;
}

function formatInlinePreviousPrice(currentPrice, previousPrice) {
  // TEMPORARILY return empty string for marketplace slim (non-creator types)
  if (state.type !== "creator") {
    return "";
  }

  const previousNumeric = resolvePreviousPriceNumber(currentPrice, previousPrice);
  if (previousNumeric === null) return "";

  const previousLabel = previousNumeric <= 0 ? "Free" : `$${Math.round(previousNumeric)}`;
  return `<span class="price-previous with-tooltip"><span class="price-prev-arrow" aria-hidden="true"><svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><path d="M6 1 L1 5 L6 9" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M2 5 H9" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></span>${previousLabel}<div class="tooltip top"><div class="tooltip-item">Price change since yesterday</div></div></span>`;
}

function renderChangeSpan(contextClass, changeInfo) {
  if (!changeInfo.title) {
    return `<span class="${contextClass} ${changeInfo.colorClass}">${changeInfo.text}</span>`;
  }
  return `<span class="${contextClass} ${changeInfo.colorClass} with-tooltip"><span class="change-text">${changeInfo.text}</span><div class="tooltip top"><div class="tooltip-item">${changeInfo.title}</div></div></span>`;
}

function renderRankWithBadge(icon, rankText, badgeClass = "") {
  const classSuffix = badgeClass ? ` ${badgeClass}` : "";
  return `<span class="rank-with-badge"><span class="rank-badge${classSuffix}">${icon}</span><span class="rank-number">${rankText}</span></span>`;
}

function isRankTrophyContext(contextKey, itemType) {
  if (itemType === "plugin") {
    return contextKey === "alltime-free" || contextKey === "alltime-paid";
  }
  if (itemType === "tutorial") {
    return contextKey === "lastWeek-all";
  }
  return contextKey === "lastWeek-free" || contextKey === "lastWeek-paid";
}

function formatStandardRank(contextKey, rank, _trophyContexts, itemType = "template") {
  const display = formatRank(rank);
  if (display === "-") return display;

  if (!isRankTrophyContext(contextKey, itemType)) {
    return display;
  }

  if (rank === 1 || rank === 2 || rank === 3) {
    return renderRankWithBadge(renderTrophySvg(rank, 24, "rank-trophy-svg"), display, "rank-trophy-badge");
  }

  return display;
}

function formatCategoryRank(_contextKey, rank, _itemType = "template") {
  if (rank === null || rank === undefined) return "-";
  return formatCategoryRankWithMedal(rank, 17, "rank-medal-svg");
}

/**
 * Render a sparkline HTML string for 7-day trend data
 *
 * Color logic:
 * - Green (#22c55e): Improving trend (rank going down = better)
 * - Red (#ef4444): Declining trend (rank going up = worse)
 * - Gray (#9ca3af): Stable trend (ranks not changing significantly)
 *
 * @param {number[]|undefined} trendData - Array of 7 rank values (oldest to newest)
 * @param {Object} options - Optional configuration
 * @param {number} options.width - SVG width in pixels (default: 80)
 * @param {number} options.height - SVG height in pixels (default: 24)
 * @param {number} options.strokeWidth - Line stroke width (default: 2)
 * @param {number|string|null} options.lastDelta - Exact current rank change,
 *   used only to color a final flat rounded segment
 * @returns {string} HTML string with sparkline span
 */
export function renderSparkline(trendData, options = {}) {
  // Handle missing or invalid data
  if (!trendData || !Array.isArray(trendData) || trendData.length === 0) {
    return `<span class="sparkline-placeholder text-muted-foreground"></span>`;
  }

  const slots = 7;
  const normalized = trendData.map((v) => {
    if (v === null || v === undefined) return null;
    const num = Number(v);
    return Number.isFinite(num) ? num : null;
  });
  const slotValues =
    normalized.length >= slots
      ? normalized.slice(normalized.length - slots)
      : Array.from({ length: slots - normalized.length }, () => null).concat(normalized);

  const values = slotValues.filter((v) => v !== null);

  // Need at least 2 points for a line chart
  if (values.length < 2) {
    return `<span class="sparkline-placeholder text-muted-foreground"></span>`;
  }

  // Configuration
  const width = options.width ?? 80;
  const height = options.height ?? 24;
  const strokeWidth = options.strokeWidth ?? 2;
  const padX = 2;
  const padTop = 2;
  const padBottom = 2;
  const plotW = Math.max(1, width - padX * 2);
  const plotH = Math.max(1, height - padTop - padBottom);
  const minRank = Math.min(...values);
  const maxRank = Math.max(...values);
  const range = Math.max(1, maxRank - minRank);
  const stepX = slots > 1 ? plotW / (slots - 1) : 0;
  const points = slotValues.map((rank, slotIndex) => {
    if (rank === null) return null;
    const x = padX + stepX * slotIndex;
    // Lower rank is better and should appear higher on chart.
    const normalized = (rank - minRank) / range;
    const y = padTop + normalized * plotH;
    return { x, y, rank };
  });
  const nonNullPoints = points.filter((p) => p !== null);
  const lastNonNullSlotIndex = points.reduce((lastIndex, point, index) => (point === null ? lastIndex : index), -1);
  const previousNonNullSlotIndex = points.reduce(
    (previousIndex, point, index) => (point === null || index === lastNonNullSlotIndex ? previousIndex : index),
    -1,
  );
  const exactLastDelta = Number(options.lastDelta);
  const finalFlatColor =
    Number.isFinite(exactLastDelta) && exactLastDelta !== 0 ? (exactLastDelta > 0 ? "#16a34a" : "#dc2626") : null;

  const firstRank = values[0];
  const lastRank = values[values.length - 1];
  const delta = firstRank - lastRank;
  const stepColor = (prevRank, currRank, isFinalSegment = false) => {
    // Lower rank number is better: 12 -> 10 is an upward move (green).
    if (currRank < prevRank) return "#16a34a";
    if (currRank > prevRank) return "#dc2626";
    if (isFinalSegment && finalFlatColor) return finalFlatColor;
    return "#9ca3af";
  };
  const segments = points
    .map((point, i) => {
      if (i === 0 || point === null) return "";
      const prev = points[i - 1];
      if (prev === null) return "";
      const isFinalSegment = i === lastNonNullSlotIndex && i - 1 === previousNonNullSlotIndex;
      const color = stepColor(prev.rank, point.rank, isFinalSegment);
      return `<line x1="${prev.x.toFixed(2)}" y1="${prev.y.toFixed(2)}" x2="${point.x.toFixed(2)}" y2="${point.y.toFixed(2)}" stroke="${color}" stroke-width="${strokeWidth}" stroke-linecap="round"></line>`;
    })
    .join("");
  const circles = points
    .map((p, i) => {
      if (p === null) return "";
      const prev = i > 0 ? points[i - 1] : null;
      const next = i < points.length - 1 ? points[i + 1] : null;
      const color =
        prev !== null ? stepColor(prev.rank, p.rank) : next !== null ? stepColor(p.rank, next.rank) : "#9ca3af";
      return `<circle cx="${p.x.toFixed(2)}" cy="${p.y.toFixed(2)}" r="1.5" fill="${color}" opacity="0.9"></circle>`;
    })
    .join("");
  const labelMode = shouldRenderSparklinePointLabels();
  const labelPoints = labelMode === "all" ? nonNullPoints : [];
  const pointLabels = labelPoints
    .map((p, i) => {
      const nonNullIndex = labelMode === "all" ? i : nonNullPoints.length - 1;
      const labelY = p.y <= 8 ? p.y + 7 : p.y - 4;
      const color =
        nonNullIndex === 0
          ? nonNullPoints[1]
            ? stepColor(nonNullPoints[0].rank, nonNullPoints[1].rank)
            : "#9ca3af"
          : stepColor(nonNullPoints[nonNullIndex - 1].rank, nonNullPoints[nonNullIndex].rank);
      return `<text x="${p.x.toFixed(2)}" y="${labelY.toFixed(2)}" text-anchor="middle" font-size="6" fill="${color}" opacity="0.95">${p.rank}</text>`;
    })
    .join("");

  const titleAttr = shouldRenderSparklineTitleAttr()
    ? ` title="7-day trend: ${firstRank} → ${lastRank} (${delta > 0 ? "+" : ""}${delta})"`
    : "";

  return `<span class="sparkline"${titleAttr}>
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" aria-hidden="true" focusable="false">
      ${segments}
      ${circles}
      ${pointLabels}
    </svg>
  </span>`;
}

/**
 * Render a single table row with ALL context spans
 *
 * New unified format:
 * - item.ranks = { all: [rank, change], free: [rank, change], paid: [rank, change], categoryId: [rank, change], ... }
 * - item.categories = ["categoryId1", "categoryId2", ...]
 * - item.type = "template" | "component"
 *
 * @param {Object} item - The unified item from ranks-data.json
 * @param {number} index - The item's index in the data array
 * @returns {string} The HTML string for the row
 */
export function renderRowWithAllSpans(item, index, renderOptions = {}) {
  if (item.type === "expert") {
    const activeContext = renderOptions.activeContext || "global-all";
    const rankEntry = item.ranks?.[activeContext] || item.ranks?.["global-all"] || [item.rank, item.change];
    const rawCountryRank = item.countryRank;
    const countryRank =
      rawCountryRank === null || rawCountryRank === undefined || rawCountryRank === "" ? NaN : Number(rawCountryRank);
    const countryCategoryId =
      (Array.isArray(item.categories)
        ? item.categories.find((categoryId) => String(categoryId).startsWith("country-"))
        : null) ||
      (typeof item.country === "string" && item.country.trim() ? `country-${item.country.trim().toLowerCase()}` : "");
    const resolvedCountryRank =
      Number.isFinite(countryRank) && countryRank > 0
        ? countryRank
        : Number(item.ranks?.[`${countryCategoryId}-all`]?.[0]);
    const activeCountryRank = activeContext.includes(countryCategoryId) ? Number(rankEntry?.[0]) : resolvedCountryRank;
    const profileUrl = typeof item.profileUrl === "string" && item.profileUrl ? item.profileUrl : item.url || "#";
    // Framer's image optimizer rejects unsupported widths such as 36. Use the
    // existing 48px slot so expert avatars return an actual image response.
    const avatar = normalizeCreatorAvatarUrl(item.avatarUrl || item.thumbnail, { width: 48, quality: 90 });
    const values = {
      rank: activeContext.startsWith("country-") || activeContext.startsWith("expert-type-")
        ? `<span class="r-${activeContext}">${formatCategoryRank(activeContext, rankEntry?.[0], item.type)}</span>`
        : `<span class="expert-rank">#${escapeHtml(rankEntry?.[0] ?? index + 1)}</span>`,
      change: renderChangeSpan(`c-${activeContext}`, formatChange(rankEntry?.[1], "all")),
      image: `<span class="expert-avatar-wrap"><img src="${escapeHtml(avatar)}" alt="" class="creator-avatar" loading="lazy" decoding="async" width="36" height="36" />${renderExpertAvailability(item)}</span>`,
      name: `<a href="${escapeHtml(profileUrl)}" target="_blank" rel="noopener noreferrer" class="table-link table-link-underline marketplace-name-link">${renderMarketplaceLinkedText(item.name || "Unknown expert")}</a>`,
      expertType: renderExpertTypes(item),
      expertPro: renderExpertPro(item),
      country: countryCategoryId
        ? `<button type="button" class="rank-context-jump-btn expert-country-btn category-jump-btn" data-category-id="${escapeHtml(countryCategoryId)}" data-item-id="${escapeHtml(item.id)}" data-item-type="expert"><span class="rank-context-name expert-country-name">${escapeHtml(formatExpertCountry(item.country))}</span>${Number.isFinite(activeCountryRank) && activeCountryRank > 0 ? `<span class="rank-context-rank expert-country-rank">${formatCategoryRankWithMedal(activeCountryRank, 17, "main-category-rank-medal")}</span>` : ""}</button>`
        : `<span class="rank-context-static expert-country"><span class="rank-context-name expert-country-name">${escapeHtml(formatExpertCountry(item.country))}</span>${Number.isFinite(activeCountryRank) && activeCountryRank > 0 ? `<span class="rank-context-rank expert-country-rank">#${activeCountryRank}</span>` : ""}</span>`,
      badges: escapeHtml(Array.isArray(item.badges) ? item.badges.join(", ") : "-"),
      bio: `<span class="expert-bio" title="${escapeHtml(item.bio || "")}">${escapeHtml(item.bio || "-")}</span>`,
    };
    const cells = getColumnsForType("expert")
      .map(
        (column) =>
          `<td class="${column.cellClass}${column.id === "rank" ? " rank-cell tabular-nums" : ""} ${column.id === "name" ? "text-left" : "text-center"} font-medium text-xs">${values[column.id] ?? "-"}</td>`,
      )
      .join("");
    return `<tr data-idx="${index}" data-id="${escapeHtml(item.id)}" data-type="expert" class="hover:bg-muted/50">${cells}</tr>`;
  }
  if (item.type === "creator") {
    const creatorUrl =
      item.creatorUrl && typeof item.creatorUrl === "string" && item.creatorUrl.length > 0
        ? item.creatorUrl
        : item.creatorSlug
          ? `https://framer.com/@${item.creatorSlug}`
          : "https://framer.com/marketplace/";
    const creatorStats = item.creatorStats || {};
    const totalTemplates = Number(creatorStats.totalTemplates || 0);
    const totalTemplatesDelta1d =
      creatorStats.totalTemplatesDelta1d === null || creatorStats.totalTemplatesDelta1d === undefined
        ? null
        : Number(creatorStats.totalTemplatesDelta1d);
    const totalComponents = Number(creatorStats.totalComponents || 0);
    const totalComponentsDelta1d =
      creatorStats.totalComponentsDelta1d === null || creatorStats.totalComponentsDelta1d === undefined
        ? null
        : Number(creatorStats.totalComponentsDelta1d);
    const totalPlugins = Number(creatorStats.totalPlugins || 0);
    const totalPluginsDelta1d =
      creatorStats.totalPluginsDelta1d === null || creatorStats.totalPluginsDelta1d === undefined
        ? null
        : Number(creatorStats.totalPluginsDelta1d);
    const totalVectors = Number(creatorStats.totalVectors || 0);
    const totalVectorsDelta1d =
      creatorStats.totalVectorsDelta1d === null || creatorStats.totalVectorsDelta1d === undefined
        ? null
        : Number(creatorStats.totalVectorsDelta1d);
    const totalTutorials = Number(creatorStats.totalTutorials || 0);
    const totalTutorialsDelta1d =
      creatorStats.totalTutorialsDelta1d === null || creatorStats.totalTutorialsDelta1d === undefined
        ? null
        : Number(creatorStats.totalTutorialsDelta1d);
    const totalItems = Number(creatorStats.totalItems || 0);
    const totalItemsDelta1d =
      creatorStats.totalItemsDelta1d === null || creatorStats.totalItemsDelta1d === undefined
        ? null
        : Number(creatorStats.totalItemsDelta1d);
    const baseCreatorLink = `<a href="${escapeHtml(creatorUrl)}" target="_blank" rel="noopener noreferrer" class="table-link table-link-underline marketplace-name-link">${renderMarketplaceLinkedText(item.name || "Unknown creator")}</a>`;
    const creatorNameContent = isFieldEnabled("creatorAvatar")
      ? `<span class="creator-avatar-slot"><img src="${escapeHtml(normalizeCreatorAvatarUrl(item.thumbnail))}" alt="" class="creator-avatar" loading="lazy" decoding="async" width="36" height="36" /></span>${baseCreatorLink}`
      : baseCreatorLink;
    const creatorColumnValues = {
      name: creatorNameContent,
      templates: formatCreatorMetricCount(totalTemplates),
      templatesdelta: formatCreatorDelta(totalTemplatesDelta1d),
      components: formatCreatorMetricCount(totalComponents),
      componentsdelta: formatCreatorDelta(totalComponentsDelta1d),
      plugins: formatCreatorMetricCount(totalPlugins),
      pluginsdelta: formatCreatorDelta(totalPluginsDelta1d),
      vectors: formatCreatorMetricCount(totalVectors),
      vectorsdelta: formatCreatorDelta(totalVectorsDelta1d),
      tutorials: formatCreatorMetricCount(totalTutorials),
      tutorialsdelta: formatCreatorDelta(totalTutorialsDelta1d),
      total: formatCreatorMetricCount(totalItems),
      totaldelta: formatCreatorDelta(totalItemsDelta1d),
    };

    const creatorCells = getColumnsForType("creator")
      .map((column) => {
        const cellValue = creatorColumnValues[column.id] ?? "-";
        return `<td class="${column.cellClass} font-medium text-xs">${cellValue}</td>`;
      })
      .join("");

    return `<tr data-idx="${index}" data-id="${item.id}" data-type="creator" class="hover:bg-muted/50">${creatorCells}</tr>`;
  }

  const isCategoryViewTrendEnabled = true;
  const activeContext = renderOptions.activeContext || null;
  const activeContextOnly = renderOptions.activeContextOnly === true;
  const deferContextHydration = renderOptions.deferContextHydration === true;
  const skipTrendRender = renderOptions.skipTrendRender === true;
  const globalTrophies = getGlobalTrophiesForItem(item);
  const trophyContexts = new Set(
    globalTrophies
      .map((crown) => `${crown.period}-${crown.market}`)
      .filter((key) => key === "lastWeek-free" || key === "lastWeek-paid"),
  );

  // Build rank spans for all contexts
  const rankSpans = [];
  const changeSpans = [];
  const trendSpans = [];

  // Standard global contexts: public alltime/lastWeek plus gated template lastmonth/lastquarter.
  const standardContexts = [];
  if (item.type === "creator") {
    standardContexts.push({ key: "creators", label: "all" });
  } else {
    const rankKeys = Object.keys(item.ranks || {});
    for (const key of rankKeys) {
      standardContexts.push({ key, label: key.split("-").pop() || "all" });
    }
    if (standardContexts.length === 0) {
      const pluralType = STATE_TO_URL_TYPE[item.type] || item.type;
      standardContexts.push({ key: `${pluralType}-global-all`, label: "all" });
    }
  }

  if (!deferContextHydration) {
    for (const { key, label } of standardContexts) {
      if (activeContextOnly && key !== activeContext) continue;
      const rankEntry = item.ranks[key] || [null, null];
      const [rank, change] = rankEntry;

      const rankDisplay = formatStandardRank(key, rank, trophyContexts, item.type);
      const changeInfo = formatChange(change, label);

      rankSpans.push(`<span class="r-${key}">${rankDisplay}</span>`);
      changeSpans.push(renderChangeSpan(`c-${key}`, changeInfo));

      const trendData = skipTrendRender ? undefined : item.trend?.[key];
      const trendHtml = skipTrendRender
        ? ""
        : renderSparkline(trendData, { width: 80, height: 24, strokeWidth: 2, lastDelta: change });
      trendSpans.push(`<span class="t-${key}">${trendHtml}</span>`);
    }

    for (const categoryId of item.categories || []) {
      const contexts = [
        `${categoryId}-all`,
        `${categoryId}-alltime-all`,
        `${categoryId}-alltime-free`,
        `${categoryId}-alltime-paid`,
        `${categoryId}-lastweek-all`,
        `${categoryId}-lastweek-free`,
        `${categoryId}-lastweek-paid`,
        `${categoryId}-lastmonth-all`,
        `${categoryId}-lastmonth-free`,
        `${categoryId}-lastmonth-paid`,
        `${categoryId}-lastquarter-all`,
        `${categoryId}-lastquarter-free`,
        `${categoryId}-lastquarter-paid`,
      ];
      for (const contextKey of contexts) {
        if (activeContextOnly && contextKey !== activeContext) continue;
        const rankEntry = item.ranks[contextKey] || [null, null];
        const [rank, change] = rankEntry;

        const rankDisplay = formatCategoryRank(contextKey, rank, item.type);
        const label = contextKey.endsWith("-free") ? "free" : contextKey.endsWith("-paid") ? "paid" : "all";
        const changeInfo = formatChange(change, label);

        rankSpans.push(`<span class="r-${contextKey}">${rankDisplay}</span>`);
        changeSpans.push(renderChangeSpan(`c-${contextKey}`, changeInfo));

        const trendData = skipTrendRender
          ? undefined
          : isCategoryViewTrendEnabled
            ? item.trend?.[contextKey]
            : undefined;
        const trendHtml = skipTrendRender
          ? ""
          : renderSparkline(trendData, { width: 80, height: 24, strokeWidth: 2, lastDelta: change });
        trendSpans.push(`<span class="t-${contextKey}">${trendHtml}</span>`);
      }
    }
  }

  const imageUrl = normalizeMarketplaceThumbnailUrl(item.thumbnailUrl || item.thumbnail || "");
  const thumbnail = imageUrl
    ? `<img src="${escapeHtml(DEFERRED_THUMBNAIL_PLACEHOLDER_URL)}" data-src="${escapeHtml(imageUrl)}" alt="" class="thumbnail-image thumbnail-deferred" loading="lazy" decoding="async" width="64" height="48" />`
    : '<div class="thumbnail-placeholder">No image</div>';

  // Format URLs
  const fullUrl = item.url.startsWith("http") ? item.url : `https://www.framer.com${item.url}`;
  const escapedPrice = escapeHtml(item.price);
  const inlinePreviousPrice = formatInlinePreviousPrice(item.price, item.previousPrice);

  // Determine price type for filtering
  const priceType = item.price === "Free" ? "Free" : "Paid";

  // Format author URL
  let authorLink = "";
  if (item.authorName && item.authorUrl && typeof item.authorUrl === "string" && item.authorUrl.length > 0) {
    let authorUrl;
    if (item.authorUrl.startsWith("http")) {
      authorUrl = item.authorUrl;
    } else if (item.authorUrl.startsWith("/creators/")) {
      authorUrl = `https://framer.com/@${item.authorUrl.replace("/creators/", "").replace(/\/$/, "")}`;
    } else {
      authorUrl = `https://www.framer.com${item.authorUrl}`;
    }
    if (isFieldEnabled("creatorAvatar")) {
      const authorAvatarUrl = normalizeCreatorAvatarUrl(
        item.creatorAvatarUrl || item.creatorAvatar || item.authorAvatar || "",
        { width: 32 },
      );
      const authorAvatarHtml = `<span class="creator-avatar-slot author-avatar-slot"><img src="${escapeHtml(DEFAULT_CREATOR_AVATAR_URL)}" data-src="${escapeHtml(authorAvatarUrl)}" alt="" class="creator-avatar author-avatar thumbnail-deferred" loading="lazy" decoding="async" width="24" height="24" /></span>`;
      authorLink = `<span class="author-with-avatar">${authorAvatarHtml}<a href="${escapeHtml(authorUrl)}" target="_blank" rel="noopener noreferrer" class="table-link table-link-underline marketplace-name-link">${renderMarketplaceLinkedText(String(item.authorName))}</a></span>`;
    } else {
      authorLink = `<a href="${escapeHtml(authorUrl)}" target="_blank" rel="noopener noreferrer" class="table-link table-link-underline marketplace-name-link">${renderMarketplaceLinkedText(String(item.authorName))}</a>`;
    }
  }

  const trophyIcons = globalTrophies
    .map((crown) => {
      const periodLabel = crown.period === "alltime" ? "All Time" : "Last Week";
      const marketLabel = crown.market === "all" ? "" : crown.market === "paid" ? "PAID " : "FREE ";
      const itemTypeLabel = getItemTypeLabel(item.type);
      const tooltipItem =
        item.type === "tutorial"
          ? `#${crown.rank} in Last Week Tutorials`
          : `#${crown.rank} in ${periodLabel} ${marketLabel}${itemTypeLabel}`;
      return `<span class="with-tooltip trophy-with-tooltip"><span class="trophy-icon trophy-icon-name">${renderTrophySvg(
        crown.rank,
        16,
        "name-trophy-svg",
      )}</span><div class="tooltip right"><button type="button" class="tooltip-item trophy-jump-btn" data-trophy-period="${crown.period}" data-trophy-market="${crown.market}" data-item-type="${item.type}">${tooltipItem}</button></div></span>`;
    })
    .join("");

  const medals = computeMedals(item);
  let medalsContent = "";
  if (medals.length > 0) {
    const medalCounts = { 1: 0, 2: 0, 3: 0 };
    for (const m of medals) {
      if (m.rank === 1 || m.rank === 2 || m.rank === 3) {
        medalCounts[m.rank] += 1;
      }
    }

    const medalsHtml = `
      <span class="medals-summary">
        <span class="medal-slot${medalCounts[1] === 0 ? " is-empty" : ""}">
          <span class="medal-icon">${renderMedalSvg(1, 13, "summary-medal-svg")}</span><span class="medal-count${medalCounts[1] === 1 ? " is-single" : ""}">${medalCounts[1]}</span>
        </span>
        <span class="medal-slot${medalCounts[2] === 0 ? " is-empty" : ""}">
          <span class="medal-icon">${renderMedalSvg(2, 13, "summary-medal-svg")}</span><span class="medal-count${medalCounts[2] === 1 ? " is-single" : ""}">${medalCounts[2]}</span>
        </span>
        <span class="medal-slot${medalCounts[3] === 0 ? " is-empty" : ""}">
          <span class="medal-icon">${renderMedalSvg(3, 13, "summary-medal-svg")}</span><span class="medal-count${medalCounts[3] === 1 ? " is-single" : ""}">${medalCounts[3]}</span>
        </span>
      </span>`;
    const itemTypeLabel = getItemTypeLabel(item.type);
    const periodSet = new Set(medals.map((m) => m.period));
    const periodLabel =
      periodSet.size === 1 ? (periodSet.has("allTime") ? "All Time" : "Last Week") : "Last Week + All Time";
    const marketSet = new Set(medals.map((m) => m.market));
    const marketLabel =
      marketSet.size === 1
        ? marketSet.has("paid")
          ? `Paid ${itemTypeLabel}`
          : `Free ${itemTypeLabel}`
        : `Free/Paid ${itemTypeLabel}`;
    const tooltipTitle =
      item.type === "tutorial"
        ? `<div class="tooltip-title">Category Medals<br><span class="tooltip-title-sub">${periodLabel}</span></div>`
        : `<div class="tooltip-title">Category Medals<br><span class="tooltip-title-sub">${periodLabel}</span><br><span class="tooltip-title-sub">${marketLabel}</span></div>`;
    const tooltipItems = medals
      .map((m) => {
        return `<button type="button" class="tooltip-item medal-jump-btn" data-category-id="${escapeHtml(m.category)}" data-rank="${m.rank}" data-market="${m.market}" data-period="${m.period}" data-item-type="${item.type}">${renderMedalSvg(m.rank, 12, "tooltip-medal-svg")} #${m.rank} in ${escapeHtml(m.categoryLabel)}</button>`;
      })
      .join("");
    medalsContent = `<div class="with-tooltip">${medalsHtml}<div class="tooltip right">${tooltipTitle}${tooltipItems}</div></div>`;
  } else {
    medalsContent = "";
  }

  const categoriesContent = renderCategoryCellContent(item, renderOptions);

  // Build categories data attribute for filtering
  const categoriesAttr = item.categories?.length > 0 ? ` data-cats="${item.categories.join(",")}"` : "";

  const standardColumnValues = {
    rank: rankSpans.join(""),
    change: changeSpans.join(""),
    toppercent: "",
    trend: trendSpans.join(""),
    price: `<span class="price-content">${escapedPrice}${inlinePreviousPrice}</span>`,
    image: thumbnail,
    name: `<div class="trophy-wrapper"><a href="${escapeHtml(fullUrl)}" target="_blank" rel="noopener noreferrer" class="table-link table-link-underline marketplace-name-link">${renderMarketplaceLinkedText(item.name)}</a><span class="trophy-icons">${trophyIcons}</span></div>`,
    categories: categoriesContent,
    medals: medalsContent,
    author: authorLink,
  };

  const standardCells = getColumnsForType(item.type)
    .map((column) => {
      if (column.id === "rank") {
        return `<td class="${column.cellClass} rank-cell text-center font-medium text-xs tabular-nums">${standardColumnValues.rank}</td>`;
      }
      if (column.id === "change") {
        return `<td class="${column.cellClass} change-cell text-center font-medium text-xs">${standardColumnValues.change}</td>`;
      }
      if (column.id === "toppercent") {
        return `<td class="${column.cellClass} toppercent-cell text-center font-medium text-xs tabular-nums">${standardColumnValues.toppercent}</td>`;
      }
      if (column.id === "trend") {
        return `<td class="${column.cellClass} trend-cell text-center font-medium text-xs">${standardColumnValues.trend}</td>`;
      }
      if (column.id === "price") {
        return `<td class="${column.cellClass} text-center font-medium text-xs">${standardColumnValues.price}</td>`;
      }
      if (column.id === "image") {
        return `<td class="${column.cellClass} text-center p-1">${standardColumnValues.image}</td>`;
      }
      if (column.id === "name") {
        return `<td class="${column.cellClass} font-medium text-xs text-left">${standardColumnValues.name}</td>`;
      }
      if (column.id === "categories") {
        return `<td class="${column.cellClass} categories-cell text-center">${standardColumnValues.categories}</td>`;
      }
      if (column.id === "medals") {
        return `<td class="${column.cellClass} medals-cell">${standardColumnValues.medals}</td>`;
      }
      if (column.id === "author") {
        return `<td class="${column.cellClass} font-medium text-xs text-left">${standardColumnValues.author}</td>`;
      }
      return `<td class="${column.cellClass}">-</td>`;
    })
    .join("");

  return `<tr data-idx="${index}" data-id="${item.id}" data-type="${item.type}" data-price="${priceType}"${categoriesAttr} class="hover:bg-muted/50">${standardCells}</tr>`;
}

export function renderContextCellsForContext(item, context, renderOptions = {}) {
  if (!item || item.type === "creator" || item.type === "expert" || !context) {
    return { context: context || null, rank: "", change: "", toppercent: "", trend: "", category: "" };
  }

  const skipTrendRender = renderOptions.skipTrendRender === true;
  const topPercentTotal = renderOptions.topPercentTotal;
  const category = stripCategoryTooltipHtml(
    renderCategoryCellContent(item, { ...renderOptions, activeContext: context }),
  );
  const globalTrophies = getGlobalTrophiesForItem(item);
  const trophyContexts = new Set(
    globalTrophies
      .map((crown) => `${crown.period}-${crown.market}`)
      .filter((key) => key === "lastWeek-free" || key === "lastWeek-paid"),
  );

  let rankHtml = "";
  let changeHtml = "";
  let topPercentHtml = "";
  let trendHtml = "";

  const isCategoryContext = (item.categories || []).some((categoryId) => context.startsWith(`${categoryId}-`));
  if (
    !isCategoryContext &&
    /^(?:(?:best30|best90)-)?(global|alltime|lastWeek|lastmonth|lastquarter|lastweek)-(all|free|paid)$/.test(context)
  ) {
    const rankEntry = item.ranks?.[context] || [null, null];
    const [rank, change] = rankEntry;
    const label = context.includes("-") ? context.split("-").pop() || "all" : "all";
    rankHtml = `<span class="r-${context}">${formatStandardRank(context, rank, trophyContexts, item.type)}</span>`;
    changeHtml = renderChangeSpan(`c-${context}`, formatChange(change, label));
    topPercentHtml = renderTopPercent(rank, topPercentTotal);
    if (!skipTrendRender) {
      trendHtml = `<span class="t-${context}">${renderSparkline(item.trend?.[context], { width: 80, height: 24, strokeWidth: 2, lastDelta: change })}</span>`;
    }
    return { context, rank: rankHtml, change: changeHtml, toppercent: topPercentHtml, trend: trendHtml, category };
  }

  const rankEntry = item.ranks?.[context] || [null, null];
  const [rank, change] = rankEntry;
  const label = context.endsWith("-free") ? "free" : context.endsWith("-paid") ? "paid" : "all";
  rankHtml = `<span class="r-${context}">${formatCategoryRank(context, rank, item.type)}</span>`;
  changeHtml = renderChangeSpan(`c-${context}`, formatChange(change, label));
  topPercentHtml = renderTopPercent(rank, topPercentTotal);
  if (!skipTrendRender) {
    trendHtml = `<span class="t-${context}">${renderSparkline(item.trend?.[context], { width: 80, height: 24, strokeWidth: 2, lastDelta: change })}</span>`;
  }
  return { context, rank: rankHtml, change: changeHtml, toppercent: topPercentHtml, trend: trendHtml, category };
}

function extractCategoryCellHtmlFromRowHtml(rowHtml) {
  if (typeof rowHtml !== "string" || rowHtml.length === 0) return "";
  const match = rowHtml.match(/<td class="[^"]*categories-cell[^"]*"[^>]*>([\s\S]*?)<\/td>/);
  return match?.[1] || "";
}

function replaceCategoryCellHtmlInRowHtml(rowHtml, nextCategoryCellHtml) {
  if (typeof rowHtml !== "string" || rowHtml.length === 0) return rowHtml || "";
  return rowHtml.replace(
    /(<td class="[^"]*categories-cell[^"]*"[^>]*>)([\s\S]*?)(<\/td>)/,
    (_match, startTag, _body, endTag) => `${startTag}${nextCategoryCellHtml}${endTag}`,
  );
}

/**
 * Render all rows at once (called once per payload refresh)
 * @param {Array} items - All items from ranks-data.json
 * @returns {Array<{html: string, itemId: string, categoryCellHtml: string, index: number}>} Base row shell objects
 */
export function renderRowRecords(items, startIndex = 0, renderOptions = {}) {
  const startTime = performance.now();
  const lazyCategoryTooltip = renderOptions.lazyCategoryTooltip !== false;

  log(`[RENDER] Rendering ${items.length} rows...`);

  const rows = items.map((item, offset) => {
    const index = startIndex + offset;
    const html = renderRowWithAllSpans(item, index, renderOptions);
    const categoryCellHtml = extractCategoryCellHtmlFromRowHtml(html);
    const htmlWithLazyCategoryTooltip =
      lazyCategoryTooltip && categoryCellHtml
        ? replaceCategoryCellHtmlInRowHtml(html, stripCategoryTooltipHtml(categoryCellHtml))
        : html;
    return {
      html: htmlWithLazyCategoryTooltip,
      itemId: String(item.id || index),
      categoryCellHtml: lazyCategoryTooltip ? "" : categoryCellHtml,
      index,
    };
  });

  const duration = Math.round(performance.now() - startTime);
  log(`[RENDER] Rendered ${rows.length} rows in ${duration}ms`);

  return rows;
}

export function renderAllRows(items, renderOptions = {}) {
  return renderRowRecords(items, 0, renderOptions);
}

// Legacy exports for backward compatibility during migration
export { escapeHtml };
